import { ApexOptions } from "apexcharts"
import { useMemo } from "react"
import ReactApexChart from "react-apexcharts"
import { dailyTotals, safeDivide } from "../../../performanceSnapshot"
import { monthDayLabels } from "../../../chartDateAxis"

/*
    CPC & CPM Trend (MPN-1530 §3.4).

    Two lines, two INDEPENDENT y-axes. A CPC of $0.28 and a CPM of $2.55 differ by an
    order of magnitude here and can differ by three on a real campaign; sharing one axis
    would flatten CPC onto the baseline and make its shape unreadable, which is the whole
    point of the chart. The ticket is explicit about this.

    Each day's value is that day's blended rate, SUM(spend) / SUM(clicks) across networks
    for the day, matching how the table below blends its total row.

    A day with no clicks has no cost per click, so it contributes no CPC point at all.
    The line joins the days either side of it and stays continuous.

    That is a deliberate reversal. This chart used to emit null for such a day and let
    Apex break the line, on the reasoning that a break was the honest rendering. Four
    rounds of bug reports later the lesson is that nobody reads a broken line as "no
    clicks that day" -- they read it as a broken chart, and they are not wrong to, since
    a gap looks identical to missing data. Rachel reported it four times against three
    different explanations.

    Connecting is not the same as inventing. We never plot a value for a day that has no
    clicks, so no false CPC appears anywhere; the line simply spans the day, the footnote
    names it, and the tooltip says what happened. The one thing we do not do is leave a
    hole and expect it to be read as information.
*/

/** "8/12", or "8/12 and 8/25", or "7/31, 8/12 and 8/25". */
function formatDays(days: string[]): string {
  const labels = days.map((d) => {
    const [, month, day] = d.split("-")
    return `${Number(month)}/${Number(day)}`
  })
  if (labels.length === 1) return labels[0]
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`
}

export default function CostTrend({ data }: { data: any }) {
  const { series, options, noClickDays } = useMemo(() => {
    // No local part-day filter any more: ChartSerializer keeps the current day out of
    // performance-by-day for every rate on the page, this one included (MPN-1567).
    const days = dailyTotals(data?.["performance-by-day"])

    /*
        {x, y} pairs rather than positional arrays, which is what keeps the line whole.

        A positional array has to put *something* at a day with no clicks, and null is
        the only truthful thing to put there -- which is exactly what breaks the line.
        Omitting the point instead means the day still exists on the axis (CPM supplies
        it) while CPC simply joins its neighbours across the slot.
    */
    const cpc = days
      .map((d) => ({ x: d.day, y: safeDivide(d.spend, d.clicks) }))
      .filter((point) => point.y !== null)
      .map((point) => ({ x: point.x, y: +(point.y as number).toFixed(4) }))

    const cpm = days
      .map((d) => ({ x: d.day, y: safeDivide(d.spend * 1000, d.impressions) }))
      .filter((point) => point.y !== null)
      .map((point) => ({ x: point.x, y: +(point.y as number).toFixed(4) }))

    const options: ApexOptions = {
      chart: {
        type: "line",
        height: 300,
        toolbar: { show: false },
        // Off for the same reason the rest of the dashboard turns them off: several
        // charts mount at once and animating them all is what made the page stutter.
        animations: { enabled: false },
        zoom: { enabled: false },
      },
      colors: ["#6366f1", "#0ea5e9"],
      stroke: { width: 2, curve: "straight" },
      /*
          A marker on every point, so a day is legible as its own reading rather than
          only as a bend in the line. Kept from the previous attempt at this chart, where
          it was load-bearing: with nulls in the series and no markers, a single null
          erased its neighbours too, because a segment needs two points and they each had
          only one. The series no longer contains nulls, so that failure cannot recur,
          but the dots are worth having on their own.

          Straight rather than smooth: a spline through daily rates invents curvature
          between points that the data does not support.
      */
      markers: { size: 3, strokeWidth: 0, hover: { size: 5 } },
      dataLabels: { enabled: false },
      grid: { borderColor: "#f1f1f1" },
      xaxis: {
        type: "category",
        // Still declared explicitly. The series are {x, y} pairs now and CPC skips days,
        // so an inferred axis would be the union of whatever each series happened to
        // carry. This keeps every campaign day on the axis whether or not it has a CPC.
        categories: days.map((d) => d.day),
        tickAmount: 8,
        labels: monthDayLabels,
      },
      yaxis: [
        {
          seriesName: "CPC",
          labels: { formatter: (v: number) => (v == null ? "" : `$${v.toFixed(2)}`) },
          title: { text: "CPC" },
        },
        {
          seriesName: "CPM",
          opposite: true,
          labels: { formatter: (v: number) => (v == null ? "" : `$${v.toFixed(2)}`) },
          title: { text: "CPM" },
        },
      ],
      legend: { position: "bottom" },
      // Shared so hovering a day reports both rates at once, which is what makes the
      // two lines comparable rather than two charts stacked on one axis.
      /*
          The line is continuous now, so a day with no clicks is no longer visible as a
          hole. That makes the hover the only place the fact survives, which is why it
          still distinguishes "nothing was spent" from "money was spent and nobody
          clicked" rather than just omitting the row.

          Indexed by the axis category rather than the series position: CPC skips days,
          so its own array index no longer corresponds to a date.
      */
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (v: number, opts: any) => {
            if (v != null) return `$${v.toFixed(2)}`
            const label = opts?.w?.globals?.categoryLabels?.[opts?.dataPointIndex]
              ?? opts?.w?.globals?.labels?.[opts?.dataPointIndex]
            const day = byDay.get(label)
            if (!day) return "n/a"
            const isCpc = opts?.seriesIndex === 0
            if (!day.spend && !day.impressions) return "no delivery this day"
            if (isCpc && !day.clicks) {
              return `no clicks — ${day.impressions.toLocaleString()} impressions, $${day.spend.toFixed(2)} spent`
            }
            if (!isCpc && !day.impressions) return "no impressions this day"
            return "n/a"
          },
        },
      },
    }

    const byDay = new Map(days.map((d) => [d.day, d]))

    // Days the line passes over rather than through. Named in the footnote so a flat
    // segment is not mistaken for a day that genuinely cost nothing.
    const noClickDays = days.filter((d) => (d.spend > 0 || d.impressions > 0) && !d.clicks)

    return {
      series: [{ name: "CPC", data: cpc }, { name: "CPM", data: cpm }],
      options,
      noClickDays,
    }
  }, [data])

  return (
    <div>
      <ReactApexChart options={options} series={series as any} type="line" height={300} />
      {/*
          Names the days the line passes over.

          Connecting the line removed the visible hole, which is what we wanted, but it
          also removed the only signal that anything happened. Without this, a segment
          spanning a no-click day looks like an ordinary two-day trend. Naming the dates
          is more useful than the old count was, since it can be checked against the
          network's own reporting.

          Only rendered when there is something to say, so a clean campaign carries no
          extra text.
      */}
      {noClickDays.length > 0 && (
        <p className="mon-panel__foot">
          No clicks on {formatDays(noClickDays.map((d) => d.day))}, so there is no cost per
          click for {noClickDays.length === 1 ? "that day" : "those days"} and the line
          continues straight across. Impressions and spend are unaffected.
        </p>
      )}
    </div>
  )
}
